import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import type {
  ForgetPasswordData,
  LoginData,
  ResetPasswordData,
  SignUpData,
  VerifyEmailData,
  VerifyResetPasswordData,
} from '../types/auth';
import { googleSignIn } from '../services/authService';
import { disconnectChatClient } from '../services/chatServices';
import type { User } from '../types/user';
import type { ApiResponse } from '../utils';
import { api } from '../utils';
import { routes } from '../lib/routes';
import {
  CANONICAL_ORIGIN,
  needsOauthRelay,
  getLaunchReturnOrigin,
} from '../lib/oauthRelay';
import toast from 'react-hot-toast';
import { Toaster } from '../components/common';
import {
  useCheckStatus,
  useEmailVerification,
  useForgetPassword,
  useLogin,
  useResetPassword,
  useSignOut,
  useSignUp,
  useVerifyResetPassword,
} from '../hooks/useAuthQuery';

interface AuthContextType {
  isAuthenticated: boolean | null;
  user: User | null;
  isLoading: boolean;
  login: (data: LoginData) => Promise<void | ApiResponse>;
  googleLogin: () => void;
  signUp: (data: SignUpData) => Promise<void | ApiResponse>;
  logout: () => Promise<void>;
  emailVerification: (data: VerifyEmailData) => Promise<void | ApiResponse>;
  forgetPassword: (data: ForgetPasswordData) => Promise<void | ApiResponse>;
  verifyResetPassword: (
    data: VerifyResetPasswordData
  ) => Promise<void | ApiResponse>;
  resetPassword: (data: ResetPasswordData) => Promise<void | ApiResponse>;
  checkStatus: () => Promise<void | User>;
  error: string | null;
  clearError: () => void;
  /** Multi-event: set when an already-onboarded user just joined another
   *  event via an invite link, so the app can ask for their goals for it. */
  pendingEventJoin: { eventId: string; eventName: string | null } | null;
  clearPendingEventJoin: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const { mutateAsync: loginMutation } = useLogin();
  const { mutateAsync: signUpMutation } = useSignUp();
  const { mutateAsync: signOutMutation } = useSignOut();
  const { mutateAsync: emailVerificationMutation } = useEmailVerification();
  const { mutateAsync: forgetPasswordMutation } = useForgetPassword();
  const { mutateAsync: verifyResetPasswordMutation } = useVerifyResetPassword();
  const { mutateAsync: resetPasswordMutation } = useResetPassword();
  const { mutateAsync: checkStatusMutation } = useCheckStatus();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingEventJoin, setPendingEventJoin] = useState<{
    eventId: string;
    eventName: string | null;
  } | null>(null);

  const clearError = () => setError(null);

  const signUp = async (data: SignUpData) => {
    setError(null);

    if (data.password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    try {
      const response = await signUpMutation(data);
      localStorage.setItem('user-email', response.result.user.email);
      // `expires_at` is masked to `null` by backend (Apr-18 security hardening),
      // so storing it here used to produce a literal "null" string that the
      // email-verification countdown parsed as Invalid Date → NaN. Countdown
      // is now frontend-driven via useResendCooldown; no need to persist
      // expiry.
      navigate(routes.emailVerification);
      return response;
    } catch (error: any) {
      // For 409 conflict (email already exists), return error for form to handle
      if (error?.code === 409 || error?.response?.status === 409) {
        return {
          code: 409,
          message: error?.message || 'This email is already registered. Please log in instead.',
        };
      }
      // For other errors, show toast
      toast.custom(t => (
        <Toaster message={error?.message as string} type='error' id={t.id} />
      ));
      return error;
    }
  };

  const login = async (data: LoginData) => {
    setError(null);

    await loginMutation(data, {
      onSuccess: async response => {
        if (!response.result.user.is_email_verified) {
          localStorage.setItem('user-email', response.result.user.email);
          // See signup(): expiry no longer persisted; countdown is frontend-driven.
          navigate(routes.emailVerification);
          return;
        }

        localStorage.setItem('auth-token', response.result.access_token);

        const userData = await checkStatus();

        if (userData && userData.onboarding_status !== 'completed') {
          navigate(routes.onboardingChat);
          return;
        }

        const pending = sessionStorage.getItem('postLoginRedirect');
        sessionStorage.removeItem('postLoginRedirect');
        navigate(pending && pending.startsWith('/') && !pending.startsWith('//') ? pending : routes.home);
        return;
      },
      onError: error => {
        toast.custom(t => (
          <Toaster message={error?.message as string} type='error' id={t.id} />
        ));
        return error;
      },
    });
  };

  const logout = async () => {
    setError(null);

    // CRITICAL: Clear ALL local state FIRST, before API call
    // This ensures logout works even if API fails
    try {
      // 1. Disconnect chat client
      await disconnectChatClient();
    } catch (e) {
      console.warn('Chat disconnect failed (non-critical):', e);
    }

    // 2. Clear localStorage - remove auth token and all user-specific data
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-email');
    localStorage.removeItem('email-code-expiry');

    // 3. Clear sessionStorage - onboarding state and other session data
    sessionStorage.clear();

    // 4. Clear React state
    setUser(null);
    setIsAuthenticated(false);

    // 5. Try API logout (but don't block on it)
    try {
      await signOutMutation(undefined, {
        onError: (error: any) => {
          // Log but don't throw - we're logging out anyway
          console.warn('Logout API failed (non-critical):', error.message);
        },
      });
    } catch (e) {
      console.warn('Logout mutation failed (non-critical):', e);
    }

    // 6. Force hard redirect - ensures all in-memory state is cleared
    window.location.href = routes.login;
  };

  const emailVerification = async (data: VerifyEmailData) => {
    await emailVerificationMutation(data, {
      onSuccess: async response => {
        toast.custom(t => (
          <Toaster
            message='Email verified successfully'
            type='success'
            id={t.id}
          />
        ));

        localStorage.setItem('auth-token', response.result.access_token);
        localStorage.removeItem('user-email');
        localStorage.removeItem('email-code-expiry');

        await checkStatus();

        navigate(routes.onboardingChat);
        return response;
      },
      onError: (error: any) => {
        toast.custom(t => (
          <Toaster message={error?.message as string} type='error' id={t.id} />
        ));
        return error;
      },
    });
  };

  const forgetPassword = async (data: ForgetPasswordData) => {
    await forgetPasswordMutation(data, {
      onSuccess: () => {
        localStorage.setItem('user-email', data.email);

        toast.custom(t => (
          <Toaster
            message='Password reset email sent successfully'
            type='success'
            id={t.id}
          />
        ));

        navigate(routes.forgotPasswordVerification);
      },
      onError: (error: any) => {
        return error;
      },
    });
  };

  const verifyResetPassword = async (data: VerifyResetPasswordData) => {
    await verifyResetPasswordMutation(data, {
      onSuccess: async response => {
        localStorage.setItem(
          'auth-token',
          response.result.reset_password_token
        );

        toast.custom(t => (
          <Toaster
            message='Password reset code verified successfully'
            type='success'
            id={t.id}
          />
        ));

        navigate(routes.resetPassword);
      },
      onError: (error: any) => {
        return error;
      },
    });
  };

  const resetPassword = async (data: ResetPasswordData) => {
    await resetPasswordMutation(data, {
      onSuccess: () => {
        toast.custom(t => (
          <Toaster
            message='Your password has been changed successfully.'
            type='success'
            id={t.id}
          />
        ));

        navigate(routes.login);
      },
      onError: (error: any) => {
        return error;
      },
    });
  };

  const googleLoginPopup = useGoogleLogin({
    onSuccess: async tokenResponse => {
      const { access_token } = tokenResponse;
      const response = await googleSignIn({ token: access_token });
      if (response.code !== 200) {
        toast.custom(t => (
          <Toaster message={response.message} type='error' id={t.id} />
        ));

        return response;
      }

      localStorage.setItem('auth-token', response.result.access_token);

      // V1.5.M10 B-P2: if this canonical-origin Google login was initiated by a
      // white-label subdomain (we're on /oauth/launch?return=<sub>), mint a
      // single-use relay code and hand the session back to that subdomain
      // instead of navigating here.
      const relayReturn = getLaunchReturnOrigin();
      if (relayReturn) {
        try {
          const res = await api.post('/auth/relay/create', {});
          const code = res?.data?.result?.code as string | undefined;
          if (code) {
            const inv = new URLSearchParams(window.location.search).get('invite');
            window.location.href =
              `${relayReturn}${routes.oauthRelay}?hc=${encodeURIComponent(code)}` +
              (inv ? `&invite=${encodeURIComponent(inv)}` : '');
            return;
          }
        } catch {
          /* fall through to normal navigation on the canonical origin */
        }
      }

      const userData = await checkStatus();

      if (userData && userData.onboarding_status !== 'completed') {
        navigate(routes.onboardingChat);
        return;
      }

      const pending = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect');
      navigate(pending && pending.startsWith('/') && !pending.startsWith('//') ? pending : routes.home);
    },
    onError: error => {
      // BUG-123 FIX: Log to Sentry and show user-facing error (was silent before)
      console.error('Google login failed:', error);
      Sentry.captureException(error, {
        tags: { auth_method: 'google', flow: 'signup' },
        extra: { error_description: error?.error_description || 'Unknown Google OAuth error' },
      });
      toast.custom(t => (
        <Toaster
          message={error?.error_description || 'Google sign in failed. Please try again or use email signup.'}
          type='error'
          id={t.id}
        />
      ));
    },
  });

  // V1.5.M10 B-P2: public entry point used by every "Continue with Google"
  // button. On a white-label subdomain the Google popup can't run (origin not
  // allowlisted), so bounce to the canonical origin's launch interstitial,
  // carrying the return origin + any pending invite; it signs in there and
  // relays the session back. On the canonical origin (and localhost/preview),
  // run the popup directly as before.
  const googleLogin = () => {
    if (needsOauthRelay()) {
      const ret = encodeURIComponent(window.location.origin);
      const inv = localStorage.getItem('pending-invite');
      window.location.href =
        `${CANONICAL_ORIGIN}${routes.oauthLaunch}?return=${ret}` +
        (inv ? `&invite=${encodeURIComponent(inv)}` : '');
      return;
    }
    googleLoginPopup();
  };

  const checkStatus = async (): Promise<void | User> => {
    await checkStatusMutation(undefined, {
      onSuccess: async response => {
        setUser(response.result);
        setIsAuthenticated(true);
        return response.result;
      },
      onError: (error: any) => {
        // Determine if this is an authentication error (401) or a network/transient error
        const errorMessage = error?.message?.toLowerCase() || '';
        const errorCode = error?.code || error?.status || error?.response?.status;

        // Check for explicit 401 status or "unauthorized" in message
        const isAuthError =
          errorCode === 401 ||
          errorCode === '401' ||
          errorMessage.includes('401') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('expired') ||
          errorMessage.includes('invalid token');

        // Check for network errors (don't clear token - might be transient)
        const isNetworkError =
          errorMessage.includes('network') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('fetch') ||
          errorMessage.includes('connection') ||
          error?.name === 'TypeError'; // fetch fails with TypeError on network error

        if (isAuthError && !isNetworkError) {
          // Token is definitely invalid - clear it
          console.warn('Auth check failed with authentication error - clearing token:', errorMessage);
          localStorage.removeItem('auth-token');
          setUser(null);
          setIsAuthenticated(false);
        } else if (isNetworkError) {
          // Network error - keep token, user might retry
          console.warn('Auth check failed with network error - keeping token for retry:', errorMessage);
          // Don't change isAuthenticated - keep previous state
          // This prevents losing session during temporary network issues
          if (isAuthenticated === null) {
            // Only on initial load, assume not authenticated if we can't verify
            setIsAuthenticated(false);
          }
        } else {
          // Unknown error - be conservative, clear token
          console.error('Auth check failed with unknown error - clearing token:', error);
          localStorage.removeItem('auth-token');
          setUser(null);
          setIsAuthenticated(false);
        }
        return error;
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  // Track session start once when user is authenticated
  const sessionTracked = React.useRef(false);
  useEffect(() => {
    if (isAuthenticated && user && !sessionTracked.current) {
      sessionTracked.current = true;
      api.post('/analytics/track', {
        event_type: 'signup', // reuse signup event type as session_start
        event_category: 'session',
        event_data: { action: 'session_start', source: 'web', referrer: document.referrer || undefined },
      }).catch(() => {});
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      checkStatus();
    } else {
      // No token - user is definitely not authenticated
      // Set both states explicitly to avoid null state issues
      setIsAuthenticated(false);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // V1.5.M2 invite funnel — capture an event invite token from the landing URL.
  // The consumer backend redirects invite links to /login?invite=<token>; we
  // persist the token so it survives the login → signup → verify → onboarding
  // journey, then redeem it once authenticated (effect below). Runs on mount,
  // before checkStatus can flip isAuthenticated true.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const invite = params.get('invite');
      if (invite) {
        localStorage.setItem('pending-invite', invite);
        // V1.5.M11 — a shareable link may carry a ?src= channel tag; stash it so
        // the JOIN is attributed to the same channel as the open it came from.
        const src = params.get('src');
        if (src) localStorage.setItem('pending-invite-src', src);
        else localStorage.removeItem('pending-invite-src');
      }
    } catch {
      /* malformed URL — nothing to capture */
    }
  }, []);

  // V1.5.M2 — once the user is authenticated (via ANY path: login, email
  // verification, or Google), redeem a pending invite: join the organiser's
  // event and attribute the signup to their link. Fires here rather than in
  // each auth handler because all of them set isAuthenticated via checkStatus.
  const inviteClaimed = React.useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !user || inviteClaimed.current) return;
    const invite = localStorage.getItem('pending-invite');
    if (!invite) return;
    inviteClaimed.current = true;
    const src = localStorage.getItem('pending-invite-src') || undefined;
    api
      .post(`/invite/${encodeURIComponent(invite)}/claim`, src ? { src } : {})
      .then((res) => {
        localStorage.removeItem('pending-invite');
        localStorage.removeItem('pending-invite-src');
        // Multi-event: a user who has already onboarded and just joined a
        // NEW event gets asked whether they're attending with the same
        // objective or a different one (fresh signups do onboarding instead,
        // which captures goals; re-claims of a known event stay silent).
        const body = res?.data?.result ?? res?.data;
        if (body?.newly_added_to_event && user?.onboarding_status === 'completed') {
          setPendingEventJoin({
            eventId: body.event_id,
            eventName: body.event_name ?? null,
          });
        }
      })
      .catch((err: any) => {
        // 4xx (other than 401) = invalid/expired invite → stop retrying.
        // 401 / 5xx / network = transient → keep the token for a later retry.
        const status = err?.code || err?.response?.status;
        if (typeof status === 'number' && status >= 400 && status < 500 && status !== 401) {
          localStorage.removeItem('pending-invite');
        }
        inviteClaimed.current = false;
      });
  }, [isAuthenticated, user]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    isLoading,
    error,
    login,
    googleLogin,
    signUp,
    logout,
    emailVerification,
    forgetPassword,
    verifyResetPassword,
    resetPassword,
    checkStatus,
    clearError,
    pendingEventJoin,
    clearPendingEventJoin: () => setPendingEventJoin(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
